<script setup lang="ts">
import { Play } from 'lucide-vue-next';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AudienceToggle from '@/components/home/AudienceToggle.vue';
import type { TenantType } from '@/config/constants/enums';
import { DEMO_VIDEO_SOURCES } from '@/config/demoVideos';
import { playMedia } from '@/utils/media';

const { t } = useI18n();

const audience = defineModel<TenantType>('audience', { required: true });

const videoRef = ref<HTMLVideoElement | null>(null);
const isPlaying = ref(false);

const activeSource = computed(() => DEMO_VIDEO_SOURCES[audience.value]);
const activeLabel = computed(() => t(`home.demo.${audience.value}.label`));
const activeCaption = computed(() => t(`home.demo.${audience.value}.caption`));

// A playback failure other than "paused before it began" puts the poster back
// so the visitor has something to press again, then propagates to the error
// sink — it is a real fault (no supported source, autoplay policy).
const play = async () => {
    isPlaying.value = true;
    await nextTick();

    if (!videoRef.value) {
        return;
    }

    try {
        await playMedia(videoRef.value);
    } catch (error) {
        isPlaying.value = false;

        throw error;
    }
};

// Switching audience must never leak the other role's audio: reset to the poster.
watch(audience, () => {
    videoRef.value?.pause();

    if (videoRef.value) {
        videoRef.value.currentTime = 0;
    }

    isPlaying.value = false;
});
</script>

<template>
    <section class="dark:bg-background relative overflow-hidden bg-white py-20 sm:py-28 lg:py-36">
        <!-- Decorative background -->
        <div class="pointer-events-none absolute inset-0">
            <div
                class="absolute top-0 left-0 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-orange-300/20 blur-3xl dark:bg-gray-800/20"
            ></div>
            <div
                class="absolute right-0 bottom-0 h-[400px] w-[400px] translate-x-1/3 translate-y-1/3 rounded-full bg-teal-300/25 blur-3xl dark:bg-gray-800/20"
            ></div>
        </div>

        <div class="relative container mx-auto px-6 lg:px-8">
            <!-- Heading -->
            <div class="mx-auto max-w-2xl text-center">
                <p class="mb-4 font-medium tracking-widest text-orange-500 dark:text-orange-400" style="font-size: 0.7rem; letter-spacing: 0.2em">
                    {{ t('home.demo.eyebrow') }}
                </p>
                <h2 class="font-display text-3xl leading-tight font-normal tracking-tight text-teal-900 sm:text-4xl lg:text-5xl dark:text-white">
                    {{ t('home.demo.heading_before') }}
                    <span class="text-orange-500 italic">{{ t('home.demo.heading_accent') }}</span>
                </h2>
                <p class="mt-4 text-base leading-relaxed text-teal-600 sm:mt-6 sm:text-lg dark:text-gray-300">
                    {{ t('home.demo.subheading') }}
                </p>

                <div class="mt-8 flex justify-center">
                    <AudienceToggle v-model="audience" />
                </div>
            </div>

            <!-- Browser device frame -->
            <div class="relative mx-auto mt-12 max-w-4xl sm:mt-16">
                <!-- Decorative offset blocks -->
                <div class="absolute -top-4 -left-4 hidden h-16 w-16 rounded-sm bg-orange-300/30 sm:block dark:bg-gray-800/30"></div>
                <div class="absolute -right-4 -bottom-4 hidden h-24 w-24 rounded-sm bg-teal-300/30 sm:block dark:bg-gray-700/30"></div>

                <div
                    class="relative overflow-hidden rounded-sm border border-teal-200 bg-white shadow-2xl shadow-teal-900/10 dark:border-gray-700 dark:bg-gray-800"
                >
                    <!-- Browser chrome -->
                    <div class="flex items-center gap-3 border-b border-teal-200 bg-teal-50 px-4 py-3 sm:px-5 dark:border-gray-700 dark:bg-gray-900">
                        <div class="flex items-center gap-1.5">
                            <span class="h-3 w-3 rounded-full bg-red-400"></span>
                            <span class="h-3 w-3 rounded-full bg-amber-400"></span>
                            <span class="h-3 w-3 rounded-full bg-green-400"></span>
                        </div>
                        <div
                            class="flex flex-1 items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1 text-xs text-teal-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        >
                            <svg class="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                            </svg>
                            <span class="truncate">{{ t('home.demo.browser_url') }}</span>
                        </div>
                    </div>

                    <!-- 16:9 stage -->
                    <div class="relative aspect-video bg-teal-900">
                        <video ref="videoRef" :src="activeSource" class="h-full w-full object-cover" preload="none" controls playsinline></video>

                        <!-- Branded poster overlay (no image asset — nothing downloads until Play) -->
                        <Transition
                            enter-active-class="transition-opacity duration-300"
                            enter-from-class="opacity-0"
                            leave-active-class="transition-opacity duration-300"
                            leave-to-class="opacity-0"
                        >
                            <button
                                v-if="!isPlaying"
                                type="button"
                                :aria-label="`${t('home.demo.play')} — ${activeLabel}`"
                                class="group absolute inset-0 flex flex-col items-center justify-center gap-4 bg-linear-to-br from-orange-500/90 via-orange-500/80 to-teal-600/90"
                                @click="play"
                            >
                                <span
                                    class="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-xl transition-transform duration-300 group-hover:scale-110 sm:h-20 sm:w-20"
                                >
                                    <Play class="ml-1 h-7 w-7 fill-orange-500 text-orange-500 sm:h-9 sm:w-9" />
                                </span>
                                <Transition
                                    mode="out-in"
                                    enter-active-class="transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                                    enter-from-class="opacity-0 translate-y-2"
                                    leave-active-class="transition-all duration-200 ease-in"
                                    leave-to-class="opacity-0"
                                >
                                    <span :key="activeLabel" class="font-display text-lg font-medium tracking-tight text-white sm:text-xl">
                                        {{ activeLabel }}
                                    </span>
                                </Transition>
                                <span class="text-xs font-medium tracking-widest text-white/80" style="letter-spacing: 0.15em">
                                    {{ t('home.demo.duration_hint') }}
                                </span>
                            </button>
                        </Transition>
                    </div>
                </div>

                <!-- Caption -->
                <Transition
                    mode="out-in"
                    enter-active-class="transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                    enter-from-class="opacity-0 translate-y-2"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-to-class="opacity-0"
                >
                    <p :key="activeCaption" class="mt-6 text-center text-sm text-teal-600 sm:text-base dark:text-gray-400">
                        {{ activeCaption }}
                    </p>
                </Transition>
            </div>
        </div>
    </section>
</template>
